import { useState, useEffect } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Gift, Check, Loader2, AlertCircle, Eye, EyeOff, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { useAppStore } from "@/store";
import { apiFetch } from "@/lib/api-fetch";

function useQueryParam(name: string) {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

type Step = "loading" | "info" | "signup" | "redeeming" | "success" | "already-pro" | "already-redeemed" | "error" | "expired";

export default function GiftRedeemPage() {
  const token = useQueryParam("token");
  const { authToken, loginUser, setCapturedEmail, sessionId } = useAppStore();

  const [step, setStep] = useState<Step>("loading");
  const [giftInfo, setGiftInfo] = useState<{ recipientEmail: string; plan: string } | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [name, setName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    if (!token) {
      setStep("error");
      setErrorMsg("No redemption token found in the URL.");
      return;
    }

    (async () => {
      try {
        const res = await apiFetch(`${baseUrl}/api/payments/gift-info?token=${encodeURIComponent(token)}`);
        if (!res.ok) {
          const data = await res.json();
          setErrorMsg(data.error || "Invalid token");
          setStep("error");
          return;
        }
        const data = await res.json();

        if (data.redeemed) {
          setStep("already-redeemed");
          return;
        }
        if (new Date(data.expiresAt) < new Date()) {
          setStep("expired");
          return;
        }

        setGiftInfo({ recipientEmail: data.recipientEmail, plan: data.plan });

        if (authToken) {
          await redeemWithToken(authToken);
        } else {
          setSignupEmail(data.recipientEmail);
          setStep("info");
        }
      } catch {
        setErrorMsg("Failed to load gift information. Please try again.");
        setStep("error");
      }
    })();
  }, [token, authToken]);

  const redeemWithToken = async (authJwt: string) => {
    setStep("redeeming");
    try {
      const res = await apiFetch(`${baseUrl}/api/payments/redeem-gift`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authJwt}`,
        },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(data.error || "Redemption failed");
        setStep("error");
        return;
      }

      if (data.alreadyRedeemed) {
        setStep("already-redeemed");
      } else if (data.alreadyPro) {
        setStep("already-pro");
      } else if (data.success) {
        setStep("success");
      } else {
        setStep("error");
        setErrorMsg("Unexpected response. Please try again.");
      }
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
      setStep("error");
    }
  };

  const handleLoginAndRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);

    try {
      const res = await apiFetch(`${baseUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: signupEmail.trim(), password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setFormError(data.error || "Login failed");
        setFormLoading(false);
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
        subscriptionStatus: data.user.subscriptionStatus ?? "none",
        subscriptionPlan: data.user.subscriptionPlan ?? null,
      });

      await redeemWithToken(data.token);
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleSignupAndRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);

    try {
      setCapturedEmail(signupEmail.trim());
      const res = await apiFetch(`${baseUrl}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: signupEmail.trim(),
          password,
          name: name.trim() || null,
          sessionId,
          username: username.trim(),
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setFormError(data.error || "Signup failed");
        setFormLoading(false);
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
        subscriptionStatus: data.user.subscriptionStatus ?? "none",
        subscriptionPlan: data.user.subscriptionPlan ?? null,
      });

      await redeemWithToken(data.token);
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const planLabel = giftInfo?.plan === "yearly" ? "Pro Annual" : "Pro Monthly";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground relative overflow-hidden px-4 py-12">
      <div className="bg-orb-1" aria-hidden="true" />
      <div className="bg-orb-2" aria-hidden="true" />
      <div className="bg-orb-3" aria-hidden="true" />
      <div className="bg-grid" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl shadow-primary/10 p-8 space-y-6">
          <div className="flex justify-center mb-2">
            <ScribPostLogo size="lg" />
          </div>

          {step === "loading" && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading your gift...</p>
            </div>
          )}

          {step === "redeeming" && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Activating your Pro membership...</p>
            </div>
          )}

          {step === "info" && giftInfo && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="text-5xl mb-3">🎁</div>
                <h1 className="font-display font-bold text-2xl mb-2">You've received a gift!</h1>
                <p className="text-muted-foreground text-sm">
                  Redeem your <strong className="text-foreground">{planLabel}</strong> ScribPost membership.
                </p>
              </div>

              <div className="border border-border rounded-xl p-4 bg-secondary/20 space-y-3">
                <p className="text-sm text-muted-foreground text-center">Sign in or create an account to redeem</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep("signup")}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                  >
                    Create account
                  </button>
                  <button
                    onClick={() => setStep("signup")}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-primary to-accent text-white hover:opacity-90 transition-opacity"
                  >
                    Sign in
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === "signup" && giftInfo && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-4xl mb-2">🎁</div>
                <h1 className="font-display font-bold text-xl mb-1">Redeem your gift</h1>
                <p className="text-xs text-muted-foreground">{planLabel} membership for {giftInfo.recipientEmail}</p>
              </div>

              {formError && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
                >
                  {formError}
                </motion.div>
              )}

              <form onSubmit={handleSignupAndRedeem} className="space-y-3">
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11 rounded-xl bg-background border-border"
                  autoComplete="name"
                  autoFocus
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="h-11 rounded-xl bg-background border-border"
                  autoComplete="email"
                  required
                />
                <Input
                  type="text"
                  placeholder="Username (4+ chars)"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11 rounded-xl bg-background border-border"
                  autoComplete="username"
                  minLength={4}
                  maxLength={30}
                  pattern="[a-zA-Z0-9_]+"
                  required
                />
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password (8+ chars)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-xl bg-background border-border pr-10"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  disabled={formLoading || !signupEmail.trim() || !username.trim() || password.length < 8}
                >
                  {formLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Create Account & Redeem Gift
                    </>
                  )}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-3 text-muted-foreground">already have an account?</span>
                </div>
              </div>

              <form onSubmit={handleLoginAndRedeem} className="space-y-3">
                <Input
                  type="text"
                  placeholder="Email or username"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="h-11 rounded-xl bg-background border-border"
                  autoComplete="username"
                />
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-xl bg-background border-border pr-10"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full h-11 rounded-xl font-semibold"
                  disabled={formLoading || !signupEmail.trim() || !password}
                >
                  {formLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Log in & Redeem Gift
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}

          {step === "success" && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl mb-2">Gift redeemed!</h1>
                <p className="text-muted-foreground text-sm">
                  Your <strong className="text-foreground">{planLabel}</strong> membership is now active. Enjoy unlimited ScribPost Pro!
                </p>
              </div>
              <Link href="/">
                <Button className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90">
                  Start Creating
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {step === "already-pro" && (
            <div className="text-center py-4 space-y-4">
              <div className="text-5xl">✨</div>
              <div>
                <h1 className="font-display font-bold text-2xl mb-2">You're already Pro!</h1>
                <p className="text-muted-foreground text-sm">
                  Your account already has an active Pro membership. Enjoy ScribPost to the fullest!
                </p>
              </div>
              <Link href="/">
                <Button className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90">
                  Go to App
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {step === "already-redeemed" && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/25 flex items-center justify-center mx-auto">
                <Gift className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl mb-2">Gift already redeemed</h1>
                <p className="text-muted-foreground text-sm">
                  This gift has already been claimed. Each gift link can only be used once.
                </p>
              </div>
              <Link href="/login">
                <Button variant="outline" className="w-full h-11 rounded-xl font-semibold">
                  Log in to your account
                </Button>
              </Link>
            </div>
          )}

          {step === "expired" && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-destructive/15 border border-destructive/25 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl mb-2">Gift link expired</h1>
                <p className="text-muted-foreground text-sm">
                  This gift link has expired. Gift links are valid for one year from purchase.
                </p>
              </div>
              <Link href="/">
                <Button variant="outline" className="w-full h-11 rounded-xl font-semibold">
                  Go to ScribPost
                </Button>
              </Link>
            </div>
          )}

          {step === "error" && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-destructive/15 border border-destructive/25 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl mb-2">Something went wrong</h1>
                <p className="text-muted-foreground text-sm">
                  {errorMsg || "Unable to process this gift link."}
                </p>
              </div>
              <Link href="/">
                <Button variant="outline" className="w-full h-11 rounded-xl font-semibold">
                  Go to ScribPost
                </Button>
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
